define('home/views/UserGeneratedDiscussionCoverView',[
    'underscore',

    'core/mixins/withRichMedia',
    'core/WindowBus',
    'core/UniqueModel',

    'home/mixins/withAllstarPopover',
    'home/mixins/withChannelAvatar',
    'core/mixins/withChannelFollowing',
    'home/mixins/withThreadSharing',

    'home/models/UserProfile',

    'home/views/RelatedDiscussionsModuleView',
    'home/views/common/DiscussionCoverBaseView',
    'home/views/DiscussionPageChannelInfoView',
    'home/views/FollowChannelPromptView',
    'home/templates/userGeneratedDiscussionCover',
    'home/templates/guidelines',
    'home/utils/confirmModal',
], function (
    _,

    withRichMedia,
    WindowBus,
    UniqueModel,

    withAllstarPopover,
    withChannelAvatar,
    withChannelFollowing,
    withThreadSharing,

    UserProfile,

    RelatedDiscussionsModuleView,
    DiscussionCoverBaseView,
    DiscussionPageChannelInfoView,
    FollowChannelPromptView,
    userGeneratedDiscussionCoverTemplate,
    guidelinesTemplate,
    confirmModal
) {
    'use strict';

    var parent = DiscussionCoverBaseView;
    var parentProto = DiscussionCoverBaseView.prototype;

    /**
     * View that displays channel info on top of the discussion page
     *
     * View expects to be backed by a DiscussionViewModel and the router instance in options.app
     */
    var UserGeneratedDiscussionCoverView = parent.extend({
        template: userGeneratedDiscussionCoverTemplate,
        templateHelpers: function () {
            var existing = parentProto.templateHelpers.apply(this, arguments);
            var thread = this.model.thread;
            var embedlyData = thread.get('content');
            var isGlobalAdmin = this.session.user.get('isGlobalAdmin');
            var canEdit = thread.get('canEdit');
            var canModerate = thread.get('canModerate');
            var isSpam = thread.get('isSpam');

            return _.defaults({
                description: thread.getFullDescription(),
                hasEmbedlyData: embedlyData.title || embedlyData.description || embedlyData.image.url,
                embedlyLink: thread.getLinks()[0],
                canEdit: canEdit,
                isGlobalAdmin: isGlobalAdmin,
                showSharePrompt: thread.get('isNew') &&
                    thread.author && thread.author.isSessionUser(),
                canModerate: canModerate,
                isSpam: isSpam,
                showSpamAlert: isSpam && canModerate,
            }, existing);
        },

        regions: _.defaults({
            followChannelPromptRegion: '[data-role=follow-channel-prompt]',
            channelInfoRegion: '[data-role=channel-info]',
            relatedStoriesRegion: '[data-role=related-stories]',
        }, parentProto.regions),

        events: _.defaults({
            'click [data-action=delete-thread]': 'deleteThread',
            'click [data-action=close-thread]': 'closeThread',
            'click [data-action=open-thread]': 'openThread',
            'click [data-action=mark-spam]': 'toggleIsSpam',
            'click [data-action=show-guidelines]': 'showGuidelines',
            'click [data-action=moderation-dropdown]': 'toggleDropdown',
            'click [data-action=blacklist-forum]': 'blacklistAuthorOnForum',
            'click [data-action=blacklist-global]': 'toggleBlacklistAuthorGlobally',
        }, parentProto.events),

        initialize: function (options) {
            parentProto.initialize.call(this, options);

            // Ensure computations for values that will be used in this view
            this.model.thread.getModeratePermissions(this.session);
            this.model.thread.getEditPermissions(this.session);

            this.listenTo(this.model.thread, {
                'change:isClosed change:isDeleted change:canEdit change:canModerate': this.render,
                'change:isSpam': this.updateSpamIndicators,
            });

            if (this.model.thread.author) {
                this.bindToAuthor();
            } else {
                this.model.thread.fetchAuthor().then(_.bind(function () {
                    this.bindToAuthor();
                    this.render();
                }, this));
            }

            var windowBus = new WindowBus();

            // posts.create messages comes directly from the embed when a user makes a comment
            this.listenTo(windowBus, 'posts.create', this.showFollowChannelPromptView);

            this.updateSpamIndicators();
        },

        updateSpamIndicators: function () {
            this.$('[data-role=spam-alert]').toggleClass('is-spam', this.model.thread.get('isSpam'));
        },

        bindToAuthor: function () {
            this.listenTo(this.model.thread.author, 'change', this.render);
        },

        onRender: function () {
            parentProto.onRender.call(this);

            var media = this.model.thread.getMedia();
            if (media && media.length)
                this.renderRichMedia(media);

            this.activateRegion(this.channelInfoRegion, _.partial(this.createChannelInfoView, this.model.channel));
            this.activateRegion(this.relatedStoriesRegion, this.createRelatedLinksView);
        },

        createRelatedLinksView: function () {
            return new RelatedDiscussionsModuleView({
                collection: this.model.coverRelatedCollection,
                className: 'module--plain',
                model: this.model.thread,
            });
        },

        /*
         * Show user a prompt to follow the channel, when the user makes a comment if:
         *   - comment is a top level comment (no parent)
         *   - comment is on a thread from this channel
         *   - follow prompt has not been shown to the user yet (view uses the withExpiration mixin)
         */
        showFollowChannelPromptView: function (postData) {
            if (
                this.model.channel && this.model.channel.primaryForum &&
                !postData.parent && postData.forum === this.model.channel.primaryForum.get('id')
            ) {
                this.activateRegion(this.followChannelPromptRegion, _.partial(
                    this.createFollowChannelPromptView, this.model.channel
                ));
            }
        },

        createChannelInfoView: function (channel) {
            return new DiscussionPageChannelInfoView({
                model: channel,
                app: this.app,
                session: this.session,
            });
        },

        createFollowChannelPromptView: function (channel) {
            return new FollowChannelPromptView({
                model: channel,
            });
        },

        blacklistAuthorOnForum: function () {
            // We need to build a full `UserProfile` here because it listens for blacklist events on the `User`
            // model so it can do additional work such as kill their discussions. `User` doesn't have all the
            // information available to do this work alone.
            this.getAuthorProfile().user.confirmBlacklist({
                forum: this.model.thread.forum.id,
                notes: 'Added via discussion page',
                retroactive: true,
            });
        },

        toggleBlacklistAuthorGlobally: function () {
            var userProfile = this.getAuthorProfile();
            var user = userProfile.user;

            if (user.get('isOnGlobalBlacklist'))
                user.removeFromBlacklist();
            else
                user.confirmBlacklist({ notes: 'Added via discussion page', retroactive: true });
        },

        toggleIsSpam: function () {
            this.model.thread.toggleIsSpam();
        },

        getAuthorProfile: function () {
            return new UniqueModel(UserProfile, this.model.thread.author.attributes);
        },

        deleteThread: function () {
            this.model.thread.confirmDelete();
        },

        closeThread: function () {
            this.model.thread.close();
        },

        openThread: function () {
            this.model.thread.open();
        },

        showGuidelines: function () {
            confirmModal.confirmationPromise(confirmModal.TYPES.CLOSE,
                guidelinesTemplate(this.model.thread.forum.channel.toJSON()));
        },

        toggleDropdown: function () {
            var $navItem = this.$('.nav-dropdown__item');
            $navItem.toggleClass('dropdown-open');
        },
    });

    withAllstarPopover.call(UserGeneratedDiscussionCoverView.prototype);

    withRichMedia.call(UserGeneratedDiscussionCoverView.prototype);
    withChannelFollowing.call(UserGeneratedDiscussionCoverView.prototype, function () {
        return this.model.channel;
    });
    withChannelAvatar.call(UserGeneratedDiscussionCoverView.prototype, function () {
        return this.model.channel;
    });
    withThreadSharing.call(UserGeneratedDiscussionCoverView.prototype);

    return UserGeneratedDiscussionCoverView;
});

